import React from "react";
import { Theme, StandardProps, TableBody } from "@material-ui/core";
import { createStyles, makeStyles } from "@material-ui/styles";
import { StyleClassKey } from "js/react/typescript";
import { AppTable, AppTableCell } from "js/react/components";
import { MediaContentRendererContainerProps } from "js/react/scenes/components/MediaManagement/Types";
import clsx from "clsx";
import AppTableHead from "js/react/components/AppTable/AppTableHead";
import AppTableRow from "js/react/components/AppTable/AppTableRow";
import { sortableTableCell, bulkSelectable } from "js/react/HOC";
import { ColumnSort, Sorting, getColumnSort } from "js/types";
import MediaListRow from "js/react/scenes/components/MediaManagement/MediaListRow";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";

const styles = (theme: Theme) =>
    createStyles({
        root: {
            display: "flex",
            flexGrow: 1,
            "& .Mui-checked .MuiIconButton-label": {
                color: theme.palette.primary.main + "!important"
            },
            "& .MuiCheckbox-root": {
                padding: theme.spacing(0.5),
                "& .MuiIconButton-label": {
                    color: theme.palette.grey["600"],
                    "& .MuiSvgIcon-root": {
                        fontSize: "1.25rem"
                    }
                }
            }
        },
        upIcon: {
            fontSize: "1.5rem"
        },
        checkboxCellWidth: {
            "& table": {
                "& thead th:first-child": {
                    maxWidth: "30px",
                    textAlign: "left",
                    padding: theme.spacing(1.7)
                },
                "& tbody tr td:first-child": {
                    maxWidth: "30px",
                    padding: theme.spacing(1.7)
                }
            }
        },
        tableHead: {
            color: theme.palette.grey["600"],
            fontWeight: 600,
            backgroundColor: theme.palette.grey["200"],
            "& span:hover": {
                color: theme.palette.grey["600"]
            }
        },
        tableCell: {
            padding: theme.spacing(1.5, 3),
            textAlign: "left",
            color: theme.palette.grey["600"]
        }
    });

type MediaListViewClassKey = StyleClassKey<typeof styles>;

const useStyles = makeStyles(styles);

export interface MediaListViewProps
    extends StandardProps<{}, MediaListViewClassKey>,
        MediaContentRendererContainerProps {
    sortOrder: Sorting;
    onSortChanged: (sort: ColumnSort) => void;
}

const SortableTableCell = sortableTableCell(AppTableCell);

interface HeadCell {
    id: string;
    label: string;
}

const headCells: HeadCell[] = [
    { id: "DisplayName", label: "Name" },
    {
        id: "LastModified",
        label: "Last Modified"
    },
    { id: "Size", label: "Size" },
    { id: "Choose", label: "Choose" },
    { id: "Action", label: "Action" }
];

const BulkSelectableRow = bulkSelectable(AppTableRow);

function Component(props: MediaListViewProps) {
    const classes = useStyles(props);
    const {
        objectListings,
        checkedValues,
        onSortChanged,
        showPrevious,
        onPrevious,
        sortOrder,
        onCheckedValuesChange,

        ...rest
    } = props;

    const listingsId = objectListings.map(x => x.identifier);

    return (
        <div
            className={clsx(classes.root, {
                [classes.checkboxCellWidth]: true
            })}
        >
            <AppTable
                style={{
                    borderCollapse: "separate",
                    borderSpacing: "0px 3px",
                    border: "none"
                }}
            >
                <AppTableHead>
                    <BulkSelectableRow
                        allowChecked
                        selection={checkedValues}
                        onSelectionChanged={onCheckedValuesChange}
                        identifier={listingsId ? listingsId : []}
                        hasBorderBottom={true}
                    >
                        {headCells.map((headCell, idx) => (
                            <SortableTableCell
                                key={idx}
                                className={`${classes.tableHead} ${classes.tableCell}`}
                                variant={"head"}
                                sort={getColumnSort(sortOrder, {
                                    col: headCell.id,
                                    position: sortOrder.length
                                })}
                                style={
                                    headCell.id !== "size"
                                        ? {
                                              textAlign: "left"
                                          }
                                        : {
                                              textAlign: "center"
                                          }
                                }
                                onSortChanged={onSortChanged}
                            >
                                {headCell.label}
                            </SortableTableCell>
                        ))}
                    </BulkSelectableRow>
                </AppTableHead>

                <TableBody>
                    {showPrevious && (
                        <AppTableRow
                            hover
                            style={{ cursor: "pointer" }}
                            onClick={onPrevious}
                        >
                            <AppTableCell />
                            <AppTableCell
                                className={classes.tableCell}
                                colSpan={5}
                            >
                                <FontAwesomeIcon
                                    icon={["fal", "level-up-alt"]}
                                    className={classes.upIcon}
                                />{" "}
                                Previous Directory
                            </AppTableCell>
                        </AppTableRow>
                    )}
                    {!objectListings.length && (
                        <AppTableRow>
                            <AppTableCell
                                className={classes.tableCell}
                                style={{ textAlign: "center" }}
                                colSpan={6}
                            >
                                No data found
                            </AppTableCell>
                        </AppTableRow>
                    )}
                    {objectListings.length
                        ? objectListings.map((f, idx) => {
                              return (
                                  <MediaListRow
                                      key={f.identifier}
                                      isChecked={checkedValues.includes(
                                          f.identifier
                                      )}
                                      media={f}
                                      {...rest}
                                      onChecked={(f, checked) => {
                                          if (checked) {
                                              onCheckedValuesChange([
                                                  ...checkedValues
                                                      .map(x => x.toString())
                                                      .filter(
                                                          x =>
                                                              x !== f.identifier
                                                      ),
                                                  f.identifier
                                              ]);
                                          } else {
                                              onCheckedValuesChange(
                                                  checkedValues
                                                      .map(x => x.toString())
                                                      .filter(
                                                          x =>
                                                              x !== f.identifier
                                                      )
                                              );
                                          }
                                      }}
                                  />
                              );
                          })
                        : null}
                </TableBody>
            </AppTable>
        </div>
    );
}

Component.defaultProps = {} as MediaListViewProps;
Component.displayName = "MediaListView";
export const MediaListView = Component;
export default MediaListView;
